/**
 * Resource ID patterns that we want to convert to links
 */
export const RESOURCE_ID_PATTERNS = {
  // Prefixed resource IDs with exactly 26 uppercase alphanumeric characters
  host: /\bhost-[A-Z0-9]{26}\b/g,
  route: /\broute-[A-Z0-9]{26}\b/g,
  role: /\brole-[A-Z0-9]{26}\b/g,
  network: /\bnetwork-[A-Z0-9]{26}\b/g,
  apiKey: /\bdnkey-[A-Z0-9]{26}\b/g,
  joinKey: /\bdnjoin-[A-Z0-9]{26}\b/g,
  oidcProvider: /\boidcprovider-[A-Z0-9]{26}\b/g,
  ca: /\bca-[A-Z0-9]{26}\b/g,
  domain: /\bdomain-[A-Z0-9]{26}\b/g,
  endpointOIDC: /\bendpointoidc-[A-Z0-9]{26}\b/g,
  org: /\borg-[A-Z0-9]{26}\b/g,
  user: /\buser-[A-Z0-9]{26}\b/g,
  totp: /\btotp-[A-Z0-9]{26}\b/g,
  log: /\blog-[A-Z0-9]{26}\b/g,
} as const;

/**
 * Maps resource types to their URL patterns.
 * Use `{id}` as a placeholder for the resource ID (e.g. `/hosts/{id}`).
 * Omit `{id}` to link to the page as-is without appending the resource ID.
 * Use `null` for resources with no dedicated page.
 */
export const RESOURCE_URL_MAP = {
  host: '/hosts/{id}',
  route: '/routes/{id}',
  role: '/roles/{id}',
  network: '/settings/networks/{id}',
  apiKey: '/settings/api-keys/{id}',
  joinKey: '/settings/join-keys/{id}',
  // No detail page when viewing as SSO admin; link to the list instead
  oidcProvider: '/settings/sso?tab=admin',
  ca: '/certificate-authorities/{id}',
  endpointOIDC: '/settings/sso/{id}',
  // These don't have dedicated detail pages
  domain: null,
  org: null,
  user: null,
  totp: null,
  log: null,
} as const satisfies Record<keyof typeof RESOURCE_ID_PATTERNS, string | null>;

export type ResourceLinkInfo = {
  id: string;
  type: keyof typeof RESOURCE_ID_PATTERNS;
  url: string | null;
};

/**
 * Extracts resource IDs from text and returns information about them
 */
export function extractResourceLinks(text: string) {
  const links: ResourceLinkInfo[] = [];

  for (const [type, pattern] of Object.entries(RESOURCE_ID_PATTERNS)) {
    const matches = text.match(pattern);
    if (matches) {
      for (const match of matches) {
        const resourceType = type as keyof typeof RESOURCE_ID_PATTERNS;
        const baseUrl = RESOURCE_URL_MAP[resourceType];
        const url = baseUrl !== null ? baseUrl.replace('{id}', match) : null;

        links.push({
          id: match,
          type: resourceType,
          url,
        });
      }
    }
  }

  return links;
}

/**
 * Replaces resource IDs in text with placeholder markers for React components
 */
export function replaceResourceIdsWithPlaceholders(text: string) {
  const allResourceLinks = extractResourceLinks(text);
  let processedText = text;

  // Filter to only linkable resources (those with URLs)
  const linkableResources = allResourceLinks.filter((link) => Boolean(link.url?.length));

  // Create unique links to avoid duplicates
  const uniqueLinks: ResourceLinkInfo[] = [];
  const seenIds = new Set<string>();

  for (const link of linkableResources) {
    if (!seenIds.has(link.id)) {
      uniqueLinks.push(link);
      seenIds.add(link.id);
    }
  }

  // Replace each unique resource ID with a placeholder
  for (const [index, link] of uniqueLinks.entries()) {
    const placeholder = `__RESOURCE_LINK_${index}__`;
    processedText = processedText.replaceAll(link.id, placeholder);
  }

  return { processedText, resourceLinks: uniqueLinks };
}
